import { Button, IconButton, Menu, MenuItem, MenuList, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useEffect, useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import axios from "axios";
import { ProjectConfig } from "Global";
import { ApiRequest } from "GlobalFunctions";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from "notistack";
import AlertDialog from "elements/AlertDialog";
import TaxesRatesEditor from "./RatesEditor";
import MenuTaxes from "../MenuTaxes";
import TaxesFilters from "./TaxesFilters";

export default function TaxesRatesList(props: any) {
    const { setLoading } = props;

    /*SNACKBAR FUNCTION FOR NOTIFICATIONS*/
    const { enqueueSnackbar } = useSnackbar();

    /*==================================
    USE STATES
    ==================================*/
    const [list, setList] = useState<any>([]);
    const [pagination, setPagination] = useState<any>({});
    const [openEditor, setOpenEditor] = useState<boolean>(false);
    const [activeItem, setActiveItem] = useState<any>(null);
    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    
    // Filter states
    const [filters, setFilters] = useState<any>({
        country: null,
        region: null,
        city: null,
        category: '',
        tax_type: null
    });

    /*==================================
    LOAD LIST
    ==================================*/
    const loadList = async () => {
        setLoading(true);

        // Build filter parameters
        const filterParams: any = {};
        
        if (filters.country) {
            filterParams.country_id = typeof filters.country === 'object' ? filters.country.value : filters.country;
        }
        if (filters.region) {
            filterParams.region_id = typeof filters.region === 'object' ? filters.region.value : filters.region;
        }
        if (filters.city) {
            filterParams.city_id = typeof filters.city === 'object' ? filters.city.value : filters.city;
        }
        if (filters.category) {
            filterParams.category = filters.category;
        }
        if (filters.tax_type) {
            filterParams.tax_type_id = typeof filters.tax_type === 'object' ? filters.tax_type.value : filters.tax_type;
        }

        await ApiRequest({
            method: 'GET',
            url: '/admin/taxes/rates/list',
            query: filterParams,
            setResponse: (response: any) => {
                if (response.status === 'success') {
                    setList(response.data);
                    setPagination(response.pagination);
                } else {
                    enqueueSnackbar('Error loading tax rates', { variant: 'error' });
                }
            }
        });

        setLoading(false);
    }

    useEffect(() => {
        loadList();
    }, [filters]);

    /*==================================
    EDIT
    ==================================*/
    const handleEdit = (id: any) => {
        setActiveItem(id);
        setOpenEditor(true);
        setAnchorEl(null);
    }

    /*==================================
    DELETE
    ==================================*/
    const handleDelete = (id: any) => {
        setActiveItem(id);
        setOpenAlert(true);
        setAnchorEl(null);
    };

    const deleteItem = () => {
        setLoading(true);
        
        axios.post(ProjectConfig.api_url + `/admin/taxes/rates/deleteItem/${activeItem}`)
            .then((response) => {
                if (response.data.status === "success") {
                    enqueueSnackbar('Tax rate deleted successfully', { variant: 'success' });
                    loadList();
                } else {
                    enqueueSnackbar(response.data.message || 'Error deleting tax rate', { variant: 'error' });
                }
            })
            .catch((err) => {
                console.log(err);
                enqueueSnackbar('Error deleting tax rate', { variant: 'error' });
            })
            .finally(() => {
                setOpenAlert(false);
                setLoading(false);
            });
    };

    /*==================================
    MENU FUNCTIONS
    ==================================*/
    const handleMenuClick = (event: any, id: any) => {
        setActiveItem(id);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    /*==================================
    ADD NEW
    ==================================*/
    const handleAdd = () => {
        setActiveItem(null);
        setOpenEditor(true);
    }

    return (
        <div className='ModuleContainer'>
            <div className="ModuleHeader">
                <div className="ComponentTitle">
                    <h1>Tax Rates</h1>
                </div>

                <MenuTaxes />

                <div className="ModuleActions">
                    <Button
                        variant="outlined"
                        color="info"
                        className="btnList"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add Tax Rate
                    </Button>
                </div>
            </div>

            <div className='ModuleContent'>
                <TaxesFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters(!showFilters)}
                />

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Region</TableCell>
                            <TableCell>City</TableCell>
                            <TableCell>Tax Type</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Rate (%)</TableCell>
                            <TableCell>Exclusive</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list && list.map((item: any) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.id}</TableCell>
                                <TableCell>{item.country_name || '-'}</TableCell>
                                <TableCell>{item.region_name || '-'}</TableCell>
                                <TableCell>{item.city_name || '-'}</TableCell>
                                <TableCell>{item.tax_type_name || '-'}</TableCell>
                                <TableCell>{item.category_name || '-'}</TableCell>
                                <TableCell>{item.rate}%</TableCell>
                                <TableCell>
                                    {item.is_exclusive ? (
                                        <span style={{ color: '#4caf50', fontWeight: 'bold' }}>Yes</span>
                                    ) : (
                                        <span style={{ color: '#757575' }}>No</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <IconButton 
                                        size="small"
                                        onClick={(e) => handleMenuClick(e, item.id)}
                                    >
                                        <MoreHorizIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!list || list.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No tax rates found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                {/* Menu for actions */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    <MenuList>
                        <MenuItem onClick={() => handleEdit(activeItem)}>
                            <EditIcon fontSize="small" style={{ marginRight: 8 }} />
                            Edit
                        </MenuItem>
                        <MenuItem onClick={() => handleDelete(activeItem)}>
                            <DeleteOutlineOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
                            Delete
                        </MenuItem>
                    </MenuList>
                </Menu>

                {/* Editor Component */}
                <TaxesRatesEditor
                    setLoading={setLoading}
                    openEditor={openEditor}
                    setOpenEditor={setOpenEditor}
                    id={activeItem}
                    loadList={loadList}
                />

                {/* Delete Alert */}
                <AlertDialog
                    open={openAlert}
                    setOpen={setOpenAlert}
                    title="Delete Tax Rate"
                    message="Are you sure you want to delete this tax rate?"
                    send={deleteItem}
                />
            </div>
        </div>
    )
}
